import { useState } from 'react';
import { useSelector } from 'react-redux';

import { AamBackendApi } from 'src/libs/aamBackendApi';
import { Logger } from 'src/libs/logger';

import { useInjection } from '@context/inversify-context-provider';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import AutoCapture from '@datachecker/autocapture';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import FaceVerify from '@datachecker/faceverify';
import { TYPES } from '@libs/InversifyType';
import { RootState } from '@store/rootReducer';

const enum SERVICES {
  FACE_VERIFY = 'FACE_VERIFY',
  AUTO_CAPTURE = 'AUTO_CAPTURE',
}
const SDK_SUPPORTED_LANGUAGES = ['de', 'en', 'es', 'fr', 'it', 'nl'];
const DEFAULT_SDK_LANGUAGE = 'en';

export const useAutoCaptureSDK = () => {
  const ammbeApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const [, setACInstance] = useState<any>(null);
  const logger = useInjection<Logger>(TYPES.Logger);

  const getSdkLanguage = () => {
    const storedLanguage = localStorage?.getItem('i18nextLng') || '';
    return SDK_SUPPORTED_LANGUAGES.includes(storedLanguage) ? storedLanguage : DEFAULT_SDK_LANGUAGE;
  };

  const initAutoCaptureSDK = async (
    containerRef: any,
    setSdkSuccess: any,
    setSdkError: any,
    setSdkCapturedDocument: any,
    dcAllowedDocument: Record<string, any>,
    sdkMode: string,
  ) => {
    if (!containerRef.current) return;
    const sdkToken = await ammbeApi.fetchDataCheckerSDKToken({ service: SERVICES.AUTO_CAPTURE });

    const sdkInitiationConfig: any = {
      CONTAINER_ID: containerRef.current.id,
      LANGUAGE: getSdkLanguage(),
      TOKEN: sdkToken.token,
      ASSETS_MODE: 'CDN',
      DESKTOP_MODE: true,
      SDK_MODE: sdkMode,
    };
    if (dcAllowedDocument) sdkInitiationConfig.ALLOWED_DOCUMENTS = dcAllowedDocument;

    const instance = new AutoCapture();
    instance.init({
      ...sdkInitiationConfig,
      onComplete: function (data: any) {
        setSdkCapturedDocument(data);
        setSdkSuccess(true);
        instance.remove();
      },
      onError: function (error: Error) {
        setSdkError(true);
        logger.error('Error from AutoCapture SDK response: ' + JSON.stringify(error));
        instance.remove();
      },
      onUserExit: function (error: Error) {
        logger.error('Error from AutoCapture SDK on user exit: ' + JSON.stringify(error));
        instance.remove();
      },
    });

    setACInstance(instance);
  };

  return initAutoCaptureSDK;
};

export const useFaceVerifySDK = () => {
  const [, setFVInstance] = useState<any>(null);
  const ammbeApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const logger = useInjection<Logger>(TYPES.Logger);
  const { tenantId } = useSelector((state: RootState) => state.tenant);
  const { userId } = useSelector((state: RootState) => state.authApp)!;

  const initFaceVerifySDK = async (
    containerRef: any,
    setSdkSuccess: any,
    setSdkError: any,
    setSdkCapturedDocument: any,
  ) => {
    if (!containerRef.current) return;
    const sdkToken = await ammbeApi.fetchDataCheckerSDKToken({
      service: SERVICES.FACE_VERIFY,
      tenantId,
      userId: userId ?? '',
    });

    const instance = new FaceVerify();
    instance.init({
      CONTAINER_ID: containerRef.current.id,
      LANGUAGE: 'en',
      TOKEN: sdkToken.token,
      onComplete: (data: any) => {
        setSdkCapturedDocument(data);
        setSdkSuccess(true);
        instance.remove();
      },
      onError: (error: Error) => {
        setSdkError(true);
        logger.error('Error from FaceVerify SDK response: ' + JSON.stringify(error));
        instance.remove();
      },
      onUserExit: (error: Error) => {
        logger.error('Error from FaceVerify SDK on user exit: ' + JSON.stringify(error));
        instance.remove();
      },
    });

    setFVInstance(instance);
  };

  return initFaceVerifySDK;
};
